/**
 * Wizard Styles Hook
 *
 * Centralizes all wizard-related styles for consistent styling across wizard forms.
 * Replaces inline styles and provides a single source of truth for styling.
 *
 * Usage:
 * const styles = useWizardStyles();
 * <div style={styles.fieldContainer}>...</div>
 */

import { theme } from 'antd';
import { controlHeightTokens } from './tokens';
import type { CSSProperties } from 'react';

export const useWizardStyles = () => {
  const { token } = theme.useToken();

  return {
    // ========================================
    // Button Styles
    // ========================================

    /**
     * Primary action buttons (Submit, Next, Create)
     * Height: 44px
     */
    primaryButton: {
      height: `${controlHeightTokens.controlHeightXL}px`,
    } as CSSProperties,

    /**
     * Secondary action buttons (Cancel, Previous, Back)
     * Height: 36px
     */
    secondaryButton: {
      height: `${controlHeightTokens.controlHeight}px`,
    } as CSSProperties,

    /**
     * Add button style (for adding items to tables/lists)
     * Height: 36px
     */
    addButton: {
      height: `${controlHeightTokens.controlHeight}px`,
    } as CSSProperties,

    // ========================================
    // Form Input Styles
    // ========================================

    /**
     * Standard form input style
     * Used for: Input, Select, DatePicker, etc.
     */
    formInput: {
      height: `${controlHeightTokens.controlHeightXL}px`,
      borderRadius: `${token.borderRadius}px`,
      width: '100%',
    } as CSSProperties,

    /**
     * Form input with error state
     */
    formInputError: {
      height: `${controlHeightTokens.controlHeightXL}px`,
      borderRadius: `${token.borderRadius}px`,
      width: '100%',
      borderColor: token.colorError,
    } as CSSProperties,

    // ========================================
    // Container Styles
    // ========================================

    /**
     * Field container with gray background
     * Groups related fields together
     */
    fieldContainer: {
      marginBottom: '32px',
      padding: '20px',
      backgroundColor: token.colorFillQuaternary,
      borderRadius: `${token.borderRadiusLG}px`,
    } as CSSProperties,

    /**
     * Content container with proper spacing
     */
    contentContainer: {
      padding: `${token.paddingLG}px`,
    } as CSSProperties,

    // ========================================
    // Typography Styles
    // ========================================

    /**
     * Field label style (small, secondary color)
     */
    fieldLabel: {
      fontSize: token.fontSizeSM,
      color: token.colorTextSecondary,
      marginBottom: '4px',
      display: 'block',
    } as CSSProperties,

    /**
     * Section title style (bold, primary color)
     */
    sectionTitle: {
      display: 'block',
      marginBottom: '16px',
      fontSize: token.fontSize,
      fontWeight: 600,
      color: token.colorText,
    } as CSSProperties,

    /**
     * Required field indicator (red asterisk)
     */
    requiredIndicator: {
      color: token.colorError,
      marginRight: '4px',
    } as CSSProperties,

    /**
     * Error message text
     */
    errorText: {
      fontSize: token.fontSizeSM,
      color: token.colorError,
      marginTop: '4px',
      display: 'block',
    } as CSSProperties,

    /**
     * Help text style (muted, small)
     */
    helpText: {
      fontSize: token.fontSizeSM,
      color: token.colorTextSecondary,
      marginTop: '4px',
      display: 'block',
    } as CSSProperties,

    // ========================================
    // Layout Constants
    // ========================================

    /**
     * Layout measurements
     */
    layout: {
      headerHeight: 60,
      footerHeight: 80,
      sidebarWidth: 280,
    },

    // ========================================
    // Wizard-Specific Styles
    // ========================================

    /**
     * Overall wizard layout container
     */
    wizardLayout: {
      display: 'flex',
      height: '100vh',
      flexDirection: 'row' as const,
    } as CSSProperties,

    /**
     * Wizard header with back button
     */
    wizardHeader: {
      height: '60px',
      padding: `0 ${token.paddingLG}px`,
      borderBottom: `1px solid ${token.colorBorder}`,
      backgroundColor: token.colorBgContainer,
      display: 'flex',
      alignItems: 'center',
    } as CSSProperties,

    /**
     * Wizard content area (scrollable)
     */
    wizardContent: {
      flex: 1,
      overflowY: 'auto' as const,
      padding: `${token.paddingXL}px`,
      backgroundColor: token.colorBgLayout,
    } as CSSProperties,

    /**
     * Wizard footer with navigation buttons
     */
    wizardFooter: {
      height: '80px',
      padding: `0 ${token.paddingLG}px`,
      borderTop: `1px solid ${token.colorBorder}`,
      backgroundColor: token.colorBgContainer,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    } as CSSProperties,

    /**
     * Footer buttons container
     */
    footerButtons: {
      display: 'flex',
      gap: `${token.marginSM}px`,
      alignItems: 'center',
    } as CSSProperties,

    /**
     * Wizard sidebar (step navigation)
     */
    wizardSidebar: {
      width: '280px',
      backgroundColor: token.colorBgContainer,
      borderRight: `1px solid ${token.colorBorder}`,
      overflowY: 'auto' as const,
      padding: `${token.paddingLG}px`,
    } as CSSProperties,

    /**
     * Sidebar container wrapper
     */
    sidebarContainer: {
      width: '280px',
      height: '100%',
      backgroundColor: token.colorBgContainer,
      borderRight: `1px solid ${token.colorBorder}`,
      display: 'flex',
      flexDirection: 'column' as const,
    } as CSSProperties,

    /**
     * Step item in sidebar (base style)
     */
    stepItem: {
      padding: `${token.paddingSM}px`,
      marginBottom: `${token.marginSM}px`,
      borderRadius: `${token.borderRadius}px`,
      cursor: 'pointer',
      transition: 'all 0.2s',
    } as CSSProperties,

    /**
     * Active step item
     */
    stepItemActive: {
      padding: `${token.paddingSM}px`,
      marginBottom: `${token.marginSM}px`,
      borderRadius: `${token.borderRadius}px`,
      cursor: 'pointer',
      transition: 'all 0.2s',
      backgroundColor: token.colorPrimaryBg,
      borderLeft: `3px solid ${token.colorPrimary}`,
    } as CSSProperties,

    /**
     * Completed step item
     */
    stepItemCompleted: {
      padding: `${token.paddingSM}px`,
      marginBottom: `${token.marginSM}px`,
      borderRadius: `${token.borderRadius}px`,
      cursor: 'pointer',
      transition: 'all 0.2s',
      opacity: 0.7,
    } as CSSProperties,

    /**
     * Step number indicator (base)
     */
    stepNumber: {
      width: `${token.sizeLG}px`,
      height: `${token.sizeLG}px`,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: token.fontSizeSM,
      fontWeight: 600,
      backgroundColor: token.colorBgTextHover,
      color: token.colorTextSecondary,
      marginRight: `${token.marginSM}px`,
    } as CSSProperties,

    /**
     * Active step number
     */
    stepNumberActive: {
      width: `${token.sizeLG}px`,
      height: `${token.sizeLG}px`,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: token.fontSizeSM,
      fontWeight: 600,
      backgroundColor: token.colorPrimary,
      color: token.colorTextLightSolid,
      marginRight: `${token.marginSM}px`,
    } as CSSProperties,

    /**
     * Completed step number
     */
    stepNumberCompleted: {
      width: `${token.sizeLG}px`,
      height: `${token.sizeLG}px`,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: token.fontSizeSM,
      fontWeight: 600,
      backgroundColor: token.colorSuccess,
      color: token.colorTextLightSolid,
      marginRight: `${token.marginSM}px`,
    } as CSSProperties,

    /**
     * Step content container
     */
    stepContent: {
      display: 'flex',
      flexDirection: 'column' as const,
      flex: 1,
    } as CSSProperties,

    /**
     * Step title (base)
     */
    stepTitle: {
      fontSize: token.fontSize,
      fontWeight: 500,
      color: token.colorText,
      marginBottom: '2px',
    } as CSSProperties,

    /**
     * Active step title
     */
    stepTitleActive: {
      fontSize: token.fontSize,
      fontWeight: 600,
      color: token.colorPrimary,
      marginBottom: '2px',
    } as CSSProperties,

    /**
     * Completed step title
     */
    stepTitleCompleted: {
      fontSize: token.fontSize,
      fontWeight: 500,
      color: token.colorTextSecondary,
      marginBottom: '2px',
    } as CSSProperties,

    /**
     * Step description text
     */
    stepDescription: {
      fontSize: token.fontSizeSM,
      color: token.colorTextSecondary,
    } as CSSProperties,

    // ========================================
    // Spacing Utilities
    // ========================================

    /**
     * Standard row spacing (between fields)
     */
    rowSpacing: {
      marginTop: '16px',
    } as CSSProperties,

    /**
     * Section spacing (between major sections)
     */
    sectionSpacing: {
      marginBottom: '32px',
    } as CSSProperties,

    /**
     * Group spacing (between field groups)
     */
    groupSpacing: {
      marginBottom: '20px',
    } as CSSProperties,
  };
};

/**
 * Type export for use in components
 */
export type WizardStyles = ReturnType<typeof useWizardStyles>;
